import React, { useState } from "react";
import { useRewards } from "../hooks/useRewards";
import { FaGift, FaLock, FaUndo, FaTrophy } from "react-icons/fa";
import { FiXCircle } from "react-icons/fi";
import { formatTimestampForDisplay } from "../utils/dateUtils";
import { getTitleForLevel } from "../utils/levelUtils";
import { playSystemSound } from "../audio/soundManager";

const EMPTY_FORM = { title: "", desc: "", milestone: "", milestoneLevel: "", icon: "gift" };

function buildRewardSuggestion(level) {
  const numericLevel = Math.max(1, Number(level) || 1);
  const nextTitle = getTitleForLevel(numericLevel + 1);

  if (numericLevel <= 5) {
    return {
      title: "Rest Protocol",
      desc: "Take a full rest day. No quests. No guilt.",
      milestone: `Hold the line until ${nextTitle}.`,
    };
  }
  if (numericLevel <= 15) {
    return {
      title: "Loadout Upgrade",
      desc: "Purchase something that improves your daily performance.",
      milestone: `Earn your next ascent: ${nextTitle}.`,
    };
  }
  if (numericLevel <= 30) {
    return {
      title: "Field Recon",
      desc: "Visit somewhere new. Expand your operational range.",
      milestone: `Unlocked after advancing toward ${nextTitle}.`,
    };
  }
  if (numericLevel <= 50) {
    return {
      title: "Elite Recovery",
      desc: "Book a professional recovery session. You've earned it.",
      milestone: `Reserved for the climb toward ${nextTitle}.`,
    };
  }
  return {
    title: "Hunter's Privilege",
    desc: "Spend a full day on whatever you want. No justification required.",
    milestone: `System recognizes your approach to ${nextTitle}.`,
  };
}

const RewardsCard = ({ user, userProfile }) => {
  const { rewards, loading, addReward, editReward, deleteReward, claimReward } = useRewards(user?.uid);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const currentLevel = Number(userProfile?.level || 1);
  const suggestion = buildRewardSuggestion(currentLevel);

  const icons = {
    gift: <FaGift color="#ffb21e" size={22} />,
    trophy: <FaTrophy color="#ffd700" size={22} />,
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditId(null);
    setForm(EMPTY_FORM);
  };

  const handleOpenCreateModal = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    playSystemSound("questOpen");
    setShowModal(true);
  };

  const handleSave = () => {
    if (!form.title.trim()) return;
    if (editId) {
      editReward(editId, {
        ...form,
        title: form.title.trim(),
        desc: form.desc.trim(),
        milestone: form.milestone.trim(),
        milestoneLevel: form.milestoneLevel ? Number(form.milestoneLevel) : null,
      });
    } else {
      addReward({
        ...form,
        title: form.title.trim(),
        desc: form.desc.trim(),
        milestone: form.milestone.trim(),
        milestoneLevel: form.milestoneLevel ? Number(form.milestoneLevel) : null,
      });
    }
    playSystemSound("questAction");
    handleCloseModal();
  };

  const handleEdit = (reward) => {
    setEditId(reward.id);
    setForm({
      title: reward.title,
      desc: reward.desc || "",
      milestone: reward.milestone || "",
      milestoneLevel: reward.milestoneLevel || "",
      icon: reward.icon || "gift"
    });
    playSystemSound("questOpen");
    setShowModal(true);
  };

  return (
    <div className="rewards-card epic-shadow">
      <h4>
        <FaGift style={{ color: "#ffb21e", marginRight: 8, verticalAlign: -2 }} />
        Rewards
      </h4>

      <div className="rewards-toolbar">
        <button
          className="login-btn rewards-toolbar__btn"
          type="button"
          onClick={handleOpenCreateModal}
        >
          + Add Reward
        </button>
      </div>

      {loading ? (
        <div className="rewards-empty-state">Loading rewards...</div>
      ) : rewards.length === 0 ? (
        <div className="rewards-empty-state">No rewards set yet.</div>
      ) : (
        <ul className="rewards-list">
          {rewards.map((r) => {
            const isLocked =
              Number(r.milestoneLevel || 0) > 0 &&
              currentLevel < Number(r.milestoneLevel);
            const isClaimed = Boolean(r.claimed);

            return (
            <li
              key={r.id}
              className={`reward-item ${r.claimed ? "reward-item--claimed" : ""} ${isLocked ? "reward-item--locked" : ""}`}
            >
              <div className="reward-item__main">
                {isLocked && (
                  <div className="reward-item__lock-overlay">
                    <span className="reward-item__lock-icon">
                      <FaLock />
                    </span>
                    <span className="reward-item__lock-copy">
                      [ LOCKED - Reach Level {r.milestoneLevel} ]
                    </span>
                  </div>
                )}
                <div className="reward-item__icon">
                  {icons[r.icon] || <FaGift color="#ffb21e" size={22} />}
                </div>

                <div className="reward-item__copy">
                  <div className="reward-item__heading">
                    <h5>{r.title}</h5>
                    {r.milestone && (
                      <span className="reward-item__milestone">Milestone: {r.milestone}</span>
                    )}
                    {r.milestoneLevel && (
                      <span className="reward-item__milestone reward-item__milestone--level">
                        Level Gate: {r.milestoneLevel}
                      </span>
                    )}
                  </div>

                  {r.desc && <p className="reward-item__desc">{r.desc}</p>}

                  {r.claimed && (
                    <div className="reward-item__claimed-at">
                      Claimed {r.claimedAt ? formatTimestampForDisplay(r.claimedAt) : "now"}
                    </div>
                  )}
                </div>
              </div>

              <div
                className={`reward-item__actions ${isLocked ? "reward-item__actions--locked" : ""}`}
              >
                {!isClaimed && !isLocked ? (
                  <button
                    className="login-btn reward-item__action-btn"
                    type="button"
                    onClick={() => {
                      playSystemSound("questAction");
                      claimReward(r.id, true);
                    }}
                  >
                    Claim
                  </button>
                ) : null}

                {isClaimed ? (
                  <button
                    className="logout-btn reward-item__action-btn"
                    type="button"
                    onClick={() => {
                      playSystemSound("questAction");
                      claimReward(r.id, false);
                    }}
                  >
                    <FaUndo style={{ marginRight: 6 }} />
                    Undo
                  </button>
                ) : null}

                {isLocked ? (
                  <div className="reward-item__lock-status">
                    <FaLock />
                    <span>Level {r.milestoneLevel} required</span>
                  </div>
                ) : null}

                <button
                  className="logout-btn reward-item__action-btn"
                  type="button"
                  onClick={() => handleEdit(r)}
                >
                  Edit
                </button>

                  <button
                    className="logout-btn reward-item__action-btn"
                    type="button"
                    onClick={() => {
                      playSystemSound("questAction");
                      deleteReward(r.id);
                    }}
                  >
                    Delete
                  </button>
              </div>
            </li>
            );
          })}
        </ul>
      )}

      {/* Modal for Add/Edit */}
      {showModal && (
        <div className="solo-modal-bg" onClick={handleCloseModal}>
          <form
            className="solo-modal reward-modal"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="solo-close"
              type="button"
              onClick={handleCloseModal}
              title="Close"
            >
              <FiXCircle />
            </button>

            <div className="reward-modal__header">
              <h2>{editId ? "Edit Reward" : "Add Reward"}</h2>
              <p className="helper reward-modal__helper">
                Create rewards for milestones, streaks, or level goals and claim them when
                you hit the target.
              </p>
            </div>

            {!editId && (
              <div className="reward-suggestion-card">
                <div className="reward-suggestion-card__label">[ SYSTEM SUGGESTION ]</div>
                <div className="reward-suggestion-card__title">{suggestion.title}</div>
                <div className="reward-suggestion-card__desc">{suggestion.desc}</div>
                <div className="reward-suggestion-card__meta">
                  Next title checkpoint: {getTitleForLevel(currentLevel + 1)}
                </div>
                <button
                  className="logout-btn reward-suggestion-card__button"
                  type="button"
                  onClick={() => {
                    playSystemSound("questAction");
                    setForm((currentForm) => ({
                      ...currentForm,
                      title: suggestion.title,
                      desc: suggestion.desc,
                      milestone: suggestion.milestone,
                    }));
                  }}
                >
                  Use this suggestion
                </button>
              </div>
            )}

            <div className="solo-field">
              <label htmlFor="rewardTitle">Title</label>
              <input
                id="rewardTitle"
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="Reward title"
                maxLength={60}
              />
            </div>

            <div className="solo-field">
              <label htmlFor="rewardDesc">Description</label>
              <input
                id="rewardDesc"
                value={form.desc}
                onChange={(e) => setForm((f) => ({ ...f, desc: e.target.value }))}
                placeholder="Reward description"
                maxLength={120}
              />
            </div>

            <div className="reward-modal__meta">
              <div className="solo-field">
                <label htmlFor="rewardMilestone">Milestone / Requirement</label>
                <input
                  id="rewardMilestone"
                  value={form.milestone}
                  onChange={(e) => setForm((f) => ({ ...f, milestone: e.target.value }))}
                  placeholder="e.g. Level 5 or 1000 XP"
                />
              </div>

              <div className="solo-field">
                <label htmlFor="rewardMilestoneLevel">Level Gate (Optional)</label>
                <input
                  id="rewardMilestoneLevel"
                  type="number"
                  min={1}
                  value={form.milestoneLevel}
                  onChange={(e) => setForm((f) => ({ ...f, milestoneLevel: e.target.value }))}
                  placeholder="e.g. 12"
                />
              </div>

              <div className="solo-field reward-modal__icon">
                <label htmlFor="rewardIcon">Icon</label>
                <select
                  id="rewardIcon"
                  value={form.icon}
                  onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}
                >
                  <option value="gift">Gift</option>
                  <option value="trophy">Trophy</option>
                </select>
              </div>
            </div>

            <div className="solo-actions reward-modal__actions">
              <button
                className="logout-btn reward-modal__action-btn"
                type="button"
                onClick={handleCloseModal}
              >
                Cancel
              </button>
              <button
                className="login-btn reward-modal__action-btn"
                type="submit"
                disabled={!form.title.trim()}
              >
                Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default RewardsCard;
